/**
 * Viewport service — the single source of "what kind of screen is this right
 * now", for the mobile initiative.
 *
 * It replaces three separate gaps:
 *   1. `isPhoneViewport()` was a point-in-time `matchMedia` read with NO change
 *      listener anywhere in the codebase, so rotating a device or resizing
 *      across the threshold never re-ran any consumer. A tablet rotated into
 *      phone width kept a 372px drawer over a ~360px canvas.
 *   2. Nothing observed `visualViewport`, so an open soft keyboard was invisible
 *      to the app and a focused input inside a floating panel had no recovery.
 *   3. Pointer kind was decided by media query alone, which is wrong on hybrid
 *      devices — a Surface reports `hover: hover` while you use its touchscreen.
 *
 * DESIGN CONSTRAINT: zero module-level side effects. Nothing runs at import —
 * no `matchMedia` call, no listener — so importing this module in jsdom or Node
 * is free and the existing `matchMedia` stub in `components-tests.js` keeps
 * working. Everything starts on `initViewportService()`, which is idempotent.
 *
 * All arithmetic lives in `shared/touch-geometry.js` so it is unit-testable
 * without a DOM; this module is wiring and publication only.
 *
 * PUBLISHED ON `<html>` (consumed by CSS; registered in RUNTIME_SET_TOKENS):
 *   data-pb-phone="true"            when the phone media query matches
 *   data-pb-pointer="coarse|fine"   live pointer register
 *   data-pb-keyboard="true"         when a soft keyboard is judged open
 *   --pb-keyboard-inset             px, 0 when no keyboard
 *   --pb-vv-height                  px, visual viewport height
 *   --pb-safe-top/-right/-bottom/-left  px, resolved safe-area insets
 */

import { rafGate } from './timing.js';
import { computeKeyboardInset, resolvePointerKind } from './touch-geometry.js';

/**
 * Canonical phone threshold, in px.
 *
 * STILL A LITERAL, deliberately (Mobile P9). The plan called for deriving this
 * from `--pb-breakpoint-phone` via `getComputedStyle` — but that is a
 * module-level side effect, and this module's whole design constraint is that
 * nothing runs at import (see the header: it is what lets `components-tests.js`
 * stub `matchMedia` and what keeps importing it free in Node).
 *
 * Deriving would also be weaker, not stronger. A runtime read that silently
 * falls back to 640 when the stylesheet has not loaded disagrees with CSS
 * *quietly*. The gate does the same job *loudly*: `MOBILE-P9: the phone
 * breakpoint is one number` asserts this literal, the token in tokens.css, and
 * every in-band `@media` width are all equal, and fails the build otherwise.
 *
 * So: four copies of 640 (here, tokens.css, and every `(max-width: 640px)`
 * block), zero chance of drift.
 */
export const PHONE_BREAKPOINT_PX = 640;
export const PHONE_MEDIA_QUERY = `(max-width: ${PHONE_BREAKPOINT_PX}px)`;
const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

const SAFE_AREA_SIDES = ['top', 'right', 'bottom', 'left'];

/**
 * Docked panels that FLOAT over the canvas instead of shrinking it.
 *
 * The agent panel is 27rem wide and the canvas only slides left by half of it
 * (--pb-canvas-open-offset-right), so roughly 210px of the canvas's right edge
 * is permanently underneath an opaque panel at --pb-z-docked-panel. Anything
 * pinned to that edge is drawn where nobody can see it.
 *
 * Resizing the canvas instead is not an option: the Desktop viewport preset
 * sizes the iframe to `width: 100%` of the wrapper, so narrowing the wrapper
 * would reflow the previewed SITE across its own breakpoints. Hence occlusion
 * is published as a measurement, and chrome moves out of the way.
 *
 * This only helps CONTAINER-anchored chrome — a toolbar pinned to the overlay's
 * corner, a popover clamped to the viewport. ELEMENT-anchored chrome (resize
 * handles, spacing badges) sits on the element's own edge by definition and
 * cannot be relocated; for those the occluded strip stays occluded.
 */
const DOCKED_PANEL_SELECTOR = '.pb-agent-panel.pb-agent-open';

const state = {
  started: false,
  phone: false,
  pointerKind: 'fine',
  keyboardInset: 0,
  viewportHeight: 0,
  dockedRightEdge: Infinity,
  safeArea: { top: 0, right: 0, bottom: 0, left: 0 },
  subscribers: new Set(),
  teardown: []
};

const hasDom = () => typeof window !== 'undefined' && typeof document !== 'undefined';
const root = () => (hasDom() ? document.documentElement : null);

function matches(query) {
  if (!hasDom() || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    // Expected flow: jsdom stubs matchMedia inconsistently across suites, and a
    // throwing media query must not take the shell down. Desktop is the safe
    // default for `phone`; `fine` is overridden by the first real pointerdown.
    return false;
  }
}

/**
 * `addEventListener` on a MediaQueryList is not universally available — Safari
 * only gained it in 14. `breakpoints.js` never subscribed at all, so this is
 * the first place in the repo that needs the legacy path.
 */
function onMediaChange(query, handler) {
  if (!hasDom() || typeof window.matchMedia !== 'function') return () => {};
  let mql;
  try {
    mql = window.matchMedia(query);
  } catch {
    // Expected flow: see `matches` above.
    return () => {};
  }
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }
  if (typeof mql.addListener === 'function') {
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }
  return () => {};
}

function setAttr(name, value) {
  const el = root();
  if (!el) return;
  if (value == null || value === false) el.removeAttribute(name);
  else el.setAttribute(name, String(value));
}

function setVar(name, value) {
  root()?.style.setProperty(name, value);
}

/**
 * Read the resolved safe-area insets.
 *
 * `env()` cannot be read from JS directly, so a probe element carries them as
 * padding and we read the computed value back. Meaningless until the viewport
 * meta carries `viewport-fit=cover` (P4) — before that every side resolves to 0,
 * which is exactly the current behaviour and therefore safe to publish early.
 */
function readSafeArea() {
  const zero = { top: 0, right: 0, bottom: 0, left: 0 };
  if (!hasDom() || typeof getComputedStyle !== 'function') return zero;

  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'width:0',
    'height:0',
    'visibility:hidden',
    'pointer-events:none',
    'padding-top:env(safe-area-inset-top,0px)',
    'padding-right:env(safe-area-inset-right,0px)',
    'padding-bottom:env(safe-area-inset-bottom,0px)',
    'padding-left:env(safe-area-inset-left,0px)'
  ].join(';');

  document.body?.appendChild(probe);
  try {
    const cs = getComputedStyle(probe);
    const out = {};
    for (const side of SAFE_AREA_SIDES) {
      const raw = parseFloat(cs.getPropertyValue(`padding-${side}`));
      out[side] = Number.isFinite(raw) ? raw : 0;
    }
    return out;
  } catch {
    // Expected flow: jsdom returns '' for env()-derived padding.
    return zero;
  } finally {
    probe.remove();
  }
}

function readViewportHeight() {
  if (!hasDom()) return 0;
  const vv = window.visualViewport;
  if (vv && Number.isFinite(vv.height)) return vv.height;
  return window.innerHeight || 0;
}

function readKeyboardInset() {
  if (!hasDom()) return 0;
  const vv = window.visualViewport;
  if (!vv) return 0;
  return computeKeyboardInset(window.innerHeight, vv.height, vv.offsetTop);
}

function notify(reason) {
  const snapshot = {
    phone: state.phone,
    coarse: state.pointerKind === 'coarse',
    pointerKind: state.pointerKind,
    keyboardInset: state.keyboardInset,
    viewportHeight: state.viewportHeight,
    safeArea: { ...state.safeArea },
    reason
  };
  for (const fn of [...state.subscribers]) {
    try {
      fn(snapshot);
    } catch {
      // A broken subscriber must not stop the others or wedge the service.
      // Deliberately silent: this fires on every resize and a toast per frame
      // would be worse than the missed update.
    }
  }
}

/** Recompute the continuous values and publish. Cheap enough to run per frame. */
function measure(reason) {
  const height = readViewportHeight();
  const inset = readKeyboardInset();
  const changed = height !== state.viewportHeight || inset !== state.keyboardInset;

  state.viewportHeight = height;
  state.keyboardInset = inset;

  setVar('--pb-vv-height', `${height}px`);
  setVar('--pb-keyboard-inset', `${inset}px`);
  setAttr('data-pb-keyboard', inset > 0 ? 'true' : null);

  if (changed || reason === 'init') notify(reason);
}

function applyPhone(next, reason) {
  if (next === state.phone && reason !== 'init') return;
  state.phone = next;
  setAttr('data-pb-phone', next ? 'true' : null);
  if (reason !== 'init') notify(reason);
}

function applyPointerKind(next, reason) {
  if (next === state.pointerKind && reason !== 'init') return;
  state.pointerKind = next;
  setAttr('data-pb-pointer', next);
  if (reason !== 'init') notify(reason);
}

function applySafeArea(reason) {
  state.safeArea = readSafeArea();
  for (const side of SAFE_AREA_SIDES) setVar(`--pb-safe-${side}`, `${state.safeArea[side]}px`);
  if (reason !== 'init') notify(reason);
}

/**
 * Viewport x where the docked panel's left edge sits, or Infinity when nothing
 * is docked.
 *
 * Deliberately `offsetLeft`/`offsetWidth` rather than `getBoundingClientRect`:
 * the panel slides in over 200ms with a `translateX`, and a rect would report
 * the animating position. The offset box is the LAYOUT box, unaffected by the
 * transform, so dependent chrome settles into its final place immediately
 * instead of chasing the animation across a dozen reflows.
 */
function readDockedRightEdge() {
  if (!hasDom()) return Infinity;
  const panel = document.querySelector(DOCKED_PANEL_SELECTOR);
  if (!panel) return Infinity;
  const width = panel.offsetWidth;
  if (!width) return Infinity;
  // offsetParent is null for a fixed element, so offsetLeft is already relative
  // to the initial containing block. Fall back to the rect where a test double
  // has no layout at all.
  const left = Number.isFinite(panel.offsetLeft)
    ? panel.offsetLeft
    : panel.getBoundingClientRect().left;
  return left;
}

function applyDockedInset(reason) {
  const next = readDockedRightEdge();
  // `init` publishes unconditionally: the starting value equals the default, so
  // a pure change-gate would leave --pb-docked-inset-right undefined until the
  // first panel opened, and a CSS consumer without a fallback would resolve to
  // nothing rather than to zero.
  if (next === state.dockedRightEdge && reason !== 'init') return;
  state.dockedRightEdge = next;
  const viewportWidth = hasDom() ? window.innerWidth || 0 : 0;
  const inset = Number.isFinite(next) ? Math.max(0, viewportWidth - next) : 0;
  setVar('--pb-docked-inset-right', `${inset}px`);
  if (reason !== 'init') notify(reason);
}

// ── public API ────────────────────────────────────────────────────────────

/** Idempotent. Safe to call from shell boot and from tests. */
export function initViewportService() {
  if (state.started || !hasDom()) return;
  state.started = true;

  applyPhone(matches(PHONE_MEDIA_QUERY), 'init');
  applyPointerKind(resolvePointerKind(undefined, matches(FINE_POINTER_QUERY)), 'init');
  applySafeArea('init');
  applyDockedInset('init');
  measure('init');

  // A docked panel opens by toggling a class on <body>, which is not a viewport
  // event — nothing else would ever tell dependent chrome to reposition. The
  // observer is the one hook, and it broadcasts through the same subscriber
  // channel so every consumer reacts the same way it reacts to a resize.
  if (typeof MutationObserver === 'function') {
    const classObserver = new MutationObserver(() => applyDockedInset('docked-panel'));
    classObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    state.teardown.push(() => classObserver.disconnect());
  }

  state.teardown.push(
    onMediaChange(PHONE_MEDIA_QUERY, (e) => applyPhone(!!e.matches, 'breakpoint'))
  );
  state.teardown.push(
    onMediaChange(FINE_POINTER_QUERY, (e) =>
      // Only a fallback: a live pointerdown always wins over the media query.
      applyPointerKind(resolvePointerKind(undefined, !!e.matches), 'pointer')
    )
  );

  // Per-EVENT pointer resolution. Capture phase so nothing can stop it, and
  // passive because it never calls preventDefault.
  const onPointerDown = (e) => {
    applyPointerKind(resolvePointerKind(e?.pointerType, matches(FINE_POINTER_QUERY)), 'pointer');
  };
  document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
  state.teardown.push(() =>
    document.removeEventListener('pointerdown', onPointerDown, { capture: true })
  );

  // The panel is `right`-anchored, so its left edge moves with the viewport.
  const onResize = rafGate(() => {
    applyDockedInset('resize');
    measure('resize');
  });
  window.addEventListener('resize', onResize, { passive: true });
  state.teardown.push(() => window.removeEventListener('resize', onResize));

  const onOrientation = rafGate(() => {
    applySafeArea('orientation');
    measure('orientation');
  });
  window.addEventListener('orientationchange', onOrientation, { passive: true });
  state.teardown.push(() => window.removeEventListener('orientationchange', onOrientation));

  const vv = window.visualViewport;
  if (vv) {
    const onVv = rafGate(() => measure('keyboard'));
    vv.addEventListener('resize', onVv, { passive: true });
    vv.addEventListener('scroll', onVv, { passive: true });
    state.teardown.push(() => {
      vv.removeEventListener('resize', onVv);
      vv.removeEventListener('scroll', onVv);
    });
  }
}

/**
 * Tests only. Removes every listener and clears everything published on
 * `<html>`, so a leak shows up as a failed assertion rather than as
 * cross-suite interference.
 */
export function destroyViewportService() {
  for (const off of state.teardown.splice(0)) {
    try {
      off();
    } catch {
      // Teardown must not throw partway and strand the remaining listeners.
    }
  }
  state.subscribers.clear();
  state.started = false;
  state.phone = false;
  state.pointerKind = 'fine';
  state.keyboardInset = 0;
  state.viewportHeight = 0;
  state.dockedRightEdge = Infinity;
  state.safeArea = { top: 0, right: 0, bottom: 0, left: 0 };

  const el = root();
  if (!el) return;
  for (const attr of ['data-pb-phone', 'data-pb-pointer', 'data-pb-keyboard']) {
    el.removeAttribute(attr);
  }
  for (const prop of ['--pb-vv-height', '--pb-keyboard-inset', '--pb-docked-inset-right']) {
    el.style.removeProperty(prop);
  }
  for (const side of SAFE_AREA_SIDES) el.style.removeProperty(`--pb-safe-${side}`);
}

/**
 * SAME SIGNATURE as the original in `breakpoints.js`, deliberately: the three
 * existing callers keep working unchanged. Before `initViewportService()` it
 * falls back to a live read, so a caller that runs early is never wrong — it
 * just does not get updates until the service starts.
 */
export function isPhoneViewport() {
  if (state.started) return state.phone;
  return matches(PHONE_MEDIA_QUERY);
}

export function isCoarsePointer() {
  if (state.started) return state.pointerKind === 'coarse';
  return resolvePointerKind(undefined, matches(FINE_POINTER_QUERY)) === 'coarse';
}

export function getPointerKind() {
  return state.started
    ? state.pointerKind
    : resolvePointerKind(undefined, matches(FINE_POINTER_QUERY));
}

export function getKeyboardInset() {
  return state.started ? state.keyboardInset : readKeyboardInset();
}

export function getViewportHeight() {
  return state.started ? state.viewportHeight : readViewportHeight();
}

export function getSafeAreaInsets() {
  return { ...(state.started ? state.safeArea : readSafeArea()) };
}

/**
 * Viewport x where a docked overlay panel starts covering the canvas, or
 * Infinity when none is open. Live-reads before the service starts so early
 * callers are never wrong.
 */
export function getDockedRightEdge() {
  return state.started ? state.dockedRightEdge : readDockedRightEdge();
}

/**
 * How many pixels of `rect`'s right edge a docked panel is covering.
 *
 * Takes the rect rather than assuming the viewport edge on purpose: a canvas
 * narrower than the viewport (the tablet/mobile presets centre a fixed-width
 * iframe) may not reach the panel at all, and shifting its chrome by a
 * viewport-derived constant would push it inward for no reason. Zero means
 * "nothing to avoid".
 */
/**
 * How many pixels of `rect`'s LEFT edge the open left rail is covering (SYS-8 F-6).
 *
 * The rail floats over the canvas like the docked panel on the right: the canvas only slides
 * by half its width (--pb-canvas-open-offset), so its left strip sits under the rail. Read
 * from the layout box, as the right edge is, so a rail mid-slide is not chased. Zero when the
 * rail is collapsed or `rect` does not reach it.
 */
export function occludedLeftPx(rect) {
  if (!hasDom() || !rect) return 0;
  const rail = document.querySelector('.pb-panel-left:not(.pb-collapsed)');
  if (!rail || !rail.offsetWidth) return 0;
  const parentLeft = rail.offsetParent ? rail.offsetParent.getBoundingClientRect().left : 0;
  const railRight = parentLeft + rail.offsetLeft + rail.offsetWidth;
  const left = Number(rect.left);
  if (!Number.isFinite(left)) return 0;
  return Math.max(0, railRight - left);
}

export function occludedRightPx(rect) {
  // The usable right edge is whichever comes first: a docked panel, or the
  // VIEWPORT itself.
  //
  // The viewport half was missing, and it cost every section action on the
  // canvas. `.pb-iframe-wrap` is `width: 100%` and, when the left rail is
  // open, `translateX(--pb-canvas-open-offset)` — an overlay panel "only
  // slides the canvas, never resizes it" (builder.css). A transform creates no
  // scroll space, so the canvas's right ~226px sits OUTSIDE the window, and
  // the GlassToolbar island — pinned top-right of that overlay — was drawn
  // off-screen with ~6px visible: `document.elementFromPoint` at the
  // Duplicate button's centre returned null at 1280, 1440 and 1728, so
  // Settings / Move up / Move down / Duplicate / Reset / Delete were
  // unreachable in the DEFAULT layout. Found by the RB-1 section suite.
  // (Same family as the LG-1 command-bar occlusion, and the History Rail
  // rule "a panel that overlays and half-shifts loses twice" — which names
  // this exact `--pb-canvas-open-offset`.)
  //
  // The docstring's promise still holds: occlusion is measured against the
  // ELEMENT. A canvas narrower than the viewport has `right <= innerWidth`,
  // so it still reports 0 and is never shifted inward for something it does
  // not touch.
  const viewportEdge =
    hasDom() && Number.isFinite(window.innerWidth) && window.innerWidth > 0
      ? window.innerWidth
      : Infinity;
  const edge = Math.min(getDockedRightEdge(), viewportEdge);
  if (!Number.isFinite(edge) || !rect) return 0;
  const right = Number(rect.right);
  if (!Number.isFinite(right)) return 0;
  return Math.max(0, right - edge);
}

/**
 * Subscribe to viewport changes.
 * @param {(snapshot: object) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onViewportChange(fn) {
  if (typeof fn !== 'function') return () => {};
  state.subscribers.add(fn);
  return () => state.subscribers.delete(fn);
}
